import { client } from '@groupe-trousseau/shared/api-client/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ENROBAGE DU CLIENT D'API — LE SEUL ENDROIT, ET C'EST TOUT SON OBJET.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `DOCTRINE.md` §4 : « **aucun appel réseau nu.** Aucun `fetch` ni `axios` en dehors du
 * client généré et de sa couche d'enrobage. C'est ce qui garantit **un seul endroit pour
 * poser le jeton d'authentification, un seul endroit pour traiter un 401, un seul endroit
 * pour consommer un 426**. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE FRONT NE VOIT PLUS AUCUN JETON, ET C'EST LA BASCULE DU LOT-4b.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Ce fichier tenait le jeton de session dans une variable de module. **Il n'en tient plus
 * aucun** : la session voyage dans un cookie `httpOnly`, donc **aucun script de cette page
 * ne peut la lire** — ni celui-ci, ni un script injecté. C'est le seul état de la bascule
 * qui compte : un jeton qu'on ne peut pas lire est un jeton qu'on ne peut pas voler.
 *
 * **CE QUE ÇA A COÛTÉ EN CODE : PRESQUE RIEN, ET C'EST LE POINT.** Parce que l'enrobage
 * est unique, changer de transport a touché **ce seul fichier** côté front. Aucune page,
 * aucun hook, aucune feature n'a bougé. C'est exactement ce que la couche contrat de
 * `DOCTRINE.md` §4 achète, et c'est la première fois que ce projet en encaisse le bénéfice.
 *
 * **DEUX CHOSES REMPLACENT LE JETON.**
 *
 * 1. **`credentials: 'include'` sur chaque appel.** Sans lui, le navigateur **n'envoie
 *    pas** le cookie vers une autre origine — et `app.swype.fr` appelant `api.swype.fr`
 *    en est une. Le symptôme serait un `401` sur une personne connectée, qui ne désigne
 *    pas sa cause.
 * 2. **Le jeton anti-CSRF, lu dans SON cookie et recopié dans un en-tête.** C'est le
 *    double envoi : le navigateur renvoie le cookie tout seul, mais **seul un script de
 *    la bonne origine peut le lire** pour le recopier. Un site tiers déclenche bien la
 *    requête avec le cookie de session, et il n'a pas l'en-tête.
 */

/**
 * **Le nom du cookie anti-CSRF, et il n'est pas `httpOnly`** — c'est précisément ce qui
 * permet de le lire ici. Le cookie de session, lui, l'est, et il n'apparaît nulle part
 * dans ce fichier.
 *
 * Le préfixe `__Secure-` tombe en développement, où `secure` est impossible sur `http://` :
 * les deux noms sont donc essayés, et l'ordre n'a pas d'importance puisqu'un seul existe.
 */
const NOMS_DU_COOKIE_CSRF = ['__Secure-swype-csrf', 'swype-csrf'] as const;

/** L'en-tête où le jeton anti-CSRF se recopie. */
const EN_TETE_CSRF = 'x-csrf-token';

function lireLeJetonCsrf(): string | null {
  for (const nom of NOMS_DU_COOKIE_CSRF) {
    for (const morceau of document.cookie.split(';')) {
      const separateur = morceau.indexOf('=');
      if (separateur === -1) continue;
      if (morceau.slice(0, separateur).trim() !== nom) continue;
      const valeur = decodeURIComponent(morceau.slice(separateur + 1).trim());
      if (valeur !== '') return valeur;
    }
  }
  return null;
}

/** Ce que l'enrobage annonce quand la session tombe, écouté par les providers. */
type SurSessionPerdue = () => void;
let surSessionPerdue: SurSessionPerdue = () => {};

export function quandLaSessionTombe(rappel: SurSessionPerdue): void {
  surSessionPerdue = rappel;
}

/**
 * **L'adresse de l'API vient de l'environnement de build, jamais d'un littéral.**
 *
 * Elle est déclarée par `VITE_API_URL`. **Ce n'est pas un secret** — c'est une adresse
 * publique que le navigateur montre à chaque requête —, mais c'est une valeur qui change
 * par environnement, donc elle ne s'écrit pas dans le code.
 *
 * **Le repli local est explicite et il ne vaut qu'en développement** : sans lui, lancer
 * `npm run dev` sans fichier d'environnement produit des requêtes vers la page elle-même,
 * et l'erreur désigne une route absente au lieu d'une variable manquante.
 */
const ADRESSE_API: string =
  (import.meta.env.VITE_API_URL) ??
  'http://127.0.0.1:3000';

/**
 * **La version du client, annoncée à chaque appel.**
 *
 * C'est le mécanisme de `DOCTRINE.md` §5 : « le client annonce sa version par un en-tête.
 * Sous un minimum configuré, l'API répond `426 Upgrade Required` ». **Quand le minimum
 * n'est pas configuré côté serveur, le mécanisme est entièrement inactif** — l'annoncer
 * ne coûte donc rien et évite d'avoir à la rajouter partout le jour où il s'active.
 */
const EN_TETE_VERSION = 'x-app-version';

export function configurerLeClient(): void {
  client.setConfig({
    baseUrl: ADRESSE_API,
    // **Sans cela, le cookie de session ne part pas** : `app.swype.fr` et
    // `api.swype.fr` sont deux origines, et un navigateur n'attache les
    // justificatifs à une autre origine que si on le lui demande.
    credentials: 'include',
  });

  client.interceptors.request.use((requete) => {
    // **Le jeton de session n'apparaît pas ici, et c'est le point de la bascule** :
    // il est `httpOnly`, donc ce code ne peut pas le lire — et le navigateur
    // l'attache tout seul.
    const csrf = lireLeJetonCsrf();
    if (csrf !== null) requete.headers.set(EN_TETE_CSRF, csrf);
    requete.headers.set(EN_TETE_VERSION, __VERSION_CLIENTE__);
    return requete;
  });

  // **Le `401` et le `426` se traitent à un seul endroit**, ce que `web/CLAUDE.md`
  // impose : « jamais dans les pages ».
  client.interceptors.response.use((reponse) => {
    if (reponse.status === 401) {
      // **La session est tombée**, et il y a exactement trois causes possibles côté
      // produit : elle a expiré, elle a été révoquée — la personne a fermé ses
      // connexions ailleurs, F-52 —, ou le compte a été anonymisé. **Le front ne les
      // distingue pas**, parce que le serveur ne les distingue pas non plus : un refus
      // qui nommerait sa cause dirait à un attaquant laquelle il a déclenchée.
      surSessionPerdue();
    }
    return reponse;
  });
}

/**
 * **Ce qu'une erreur d'API dit à un écran, et rien de plus.**
 *
 * `DOCTRINE.md` §5 : « **`code` est le contrat réel** : c'est sur lui que le front branche
 * son comportement, **jamais sur `message`**, qui est destiné à l'humain et peut changer
 * sans préavis. » Cette fonction extrait donc les deux séparément, et les écrans branchent
 * sur `code`.
 */
export type ErreurDApi = {
  readonly code: string;
  readonly message: string;
  readonly statut: number | null;
  /**
   * **Ce que le refus porte EN PLUS de sa phrase — et c'est une règle du corpus.**
   *
   * `DOCTRINE.md` §5 donne l'enveloppe : `{ statusCode, code, message, details }`. Le
   * quatrième champ n'était pas lu ici, et **deux refus du produit en dépendent** :
   * « le message porte **la place restante et ce que le dépôt demande** » (Q367,
   * Q455), et « le refus **dit le geste qui le lève** » (DD-19). Sans lui, un écran ne
   * peut que recopier la phrase du serveur, qui ne porte pas les nombres.
   *
   * **Il est `unknown` par valeur, et c'est délibéré** : ce qu'un code y met dépend du
   * code, et un écran qui le lit doit le vérifier — les charges d'erreur ne sont pas
   * plus typées que celles d'un socket, et D-110 §1 a payé cette leçon.
   */
  readonly details: Record<string, unknown> | null;
};

const MESSAGE_DE_REPLI =
  'Le produit n’a pas pu joindre le serveur. Réessayez dans un instant.';

/**
 * **Déballe une réponse du client généré, ou lève.**
 *
 * Le client rend `{ data, error }` où **les deux sont facultatifs** : `data` est typé
 * `T | undefined` même quand `error` est absent, parce que le générateur ne sait pas
 * qu'une route qui répond `200` porte toujours un corps. **Déballer à la main dans chaque
 * hook produirait la même garde recopiée quinze fois**, et la quinzième serait oubliée.
 *
 * Lever plutôt que rendre un repli : une réponse vide là où le contrat en promet une est
 * un défaut de contrat, et le traiter comme une absence de données le ferait passer pour
 * une liste vide.
 */
export async function deballer<T>(
  appel: Promise<{ data?: T; error?: unknown }>,
): Promise<T> {
  const reponse = await appel;
  // **L'enveloppe d'erreur du contrat n'est PAS une `Error`, et ne le sera pas.**
  // `DOCTRINE.md` §5 en fixe la forme : `{ code, message, statusCode }`, du JSON rendu
  // par le serveur. L'emballer dans une `Error` obligerait chaque appelant à la
  // déballer, et `lireLErreur` ci-dessous existe précisément pour lire cette forme-là.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (reponse.error !== undefined) throw reponse.error;
  if (reponse.data === undefined) {
    throw new Error(
      'Le serveur a répondu sans corps là où le contrat en déclare un.',
    );
  }
  return reponse.data;
}

export function lireLErreur(erreur: unknown): ErreurDApi {
  const enveloppe = erreur as
    | {
        code?: unknown;
        message?: unknown;
        statusCode?: unknown;
        details?: unknown;
      }
    | null
    | undefined;

  return {
    code: typeof enveloppe?.code === 'string' ? enveloppe.code : 'INJOIGNABLE',
    message:
      typeof enveloppe?.message === 'string'
        ? enveloppe.message
        : MESSAGE_DE_REPLI,
    statut:
      typeof enveloppe?.statusCode === 'number' ? enveloppe.statusCode : null,
    details:
      typeof (enveloppe as { details?: unknown })?.details === 'object' &&
      (enveloppe as { details?: unknown }).details !== null
        ? ((enveloppe as { details: Record<string, unknown> }).details)
        : null,
  };
}
